import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import AnalyticsKpiCard from '../components/AnalyticsKpiCard';
import { BACKEND_URL } from '../config';
import { usePlan } from '../context/PlanContext';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';
import RunAgentModal from '../components/RunAgentModal';

import {
  ArrowLeft,
  ChatText,
  CheckCircle,
  CircleNotch,
  Clock,
  Play,
  Pulse,
  Robot,
  TrendUp,
  WarningCircle
} from '@phosphor-icons/react';
const AgentAnalyticsDashboard = () => {
  const { userPlan } = usePlan();
  const { activeOrganization } = useOrganization();
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [agentsData, setAgentsData] = useState([]);
  const [agentDetail, setAgentDetail] = useState(null);
  const [dateRange, setDateRange] = useState('30'); // 7, 30, 90 days
  const [agentToRun, setAgentToRun] = useState(null);
  const [error, setError] = useState(null);

  // Re-fetch when organization changes - CRITICAL for multi-tenant security
  useEffect(() => {
    const hasAccess = userPlan === 'pro' || userPlan === 'partner-pro' || userPlan === 'partner_pro';
    if (hasAccess && activeOrganization) {
      // Reset state when org changes to prevent stale data leakage
      setSummary(null);
      setAgentsData([]);
      setAgentDetail(null);
      
      if (agentId) {
        fetchAgentDetail();
      } else {
        fetchAnalytics();
      }
    }
  }, [userPlan, dateRange, agentId, activeOrganization?.organization_id]);

  const getDateRange = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - parseInt(dateRange));
    return {
      start_date: start.toISOString().split('T')[0],
      end_date: end.toISOString().split('T')[0]
    };
  };

  const fetchAnalytics = async () => {
    if (!activeOrganization) return;
    
    setLoading(true);
    setError(null);
    
    const orgId = activeOrganization.organization_id;
    console.log(`[Analytics] Fetching for org: ${orgId}`);
    
    try {
      const { start_date, end_date } = getDateRange();
      
      // Fetch summary - use fetchWithOrg for proper org scoping
      const summaryResponse = await fetchWithOrg(
        `${BACKEND_URL}/api/analytics/summary?start_date=${start_date}&end_date=${end_date}`,
        {},
        orgId
      );
      if (summaryResponse.ok) {
        const summaryData = await summaryResponse.json();
        console.log(`[Analytics] Summary received for org ${summaryData.org_id || 'unknown'}`);
        setSummary(summaryData);
      }

      // Fetch per-agent analytics - use fetchWithOrg for proper org scoping
      const agentsResponse = await fetchWithOrg(
        `${BACKEND_URL}/api/analytics/agents?start_date=${start_date}&end_date=${end_date}`,
        {},
        orgId
      );
      if (agentsResponse.ok) {
        const data = await agentsResponse.json();
        console.log(`[Analytics] Agents received: ${data.total_agents} for org ${data.org_id || 'unknown'}`);
        
        // SECURITY CHECK: Filter out any agents that don't belong to current org
        const filteredAgents = (data.agents || []).filter(agent => {
          const agentOrgId = agent.org_id || agent.organization_id;
          if (agentOrgId && agentOrgId !== orgId) {
            console.error(`[Analytics] TENANT LEAK DETECTED: Agent ${agent.agent_id} belongs to ${agentOrgId}, not ${orgId}`);
            return false;
          }
          return true;
        });
        
        setAgentsData(filteredAgents);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAgentDetail = async () => {
    if (!activeOrganization) return;
    
    const orgId = activeOrganization.organization_id;
    
    setLoading(true);
    setError(null);
    try {
      const { start_date, end_date } = getDateRange();
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/analytics/agents/${agentId}?start_date=${start_date}&end_date=${end_date}`,
        {},
        orgId
      );
      if (response.ok) {
        const data = await response.json();
        setAgentDetail(data);
      } else if (response.status === 404) {
        setError('Agent not found');
      }
    } catch (error) {
      console.error('Error fetching agent detail:', error);
      setError('Failed to load agent analytics');
    } finally {
      setLoading(false);
    }
  };

  const calculateSuccessRate = (success, total) => {
    if (total === 0) return 0;
    return Math.round((success / total) * 100);
  };

  // Pro tier gating (available to Pro and Partner Pro)
  const hasAccess = userPlan === 'pro' || userPlan === 'partner-pro' || userPlan === 'partner_pro';
  
  if (!hasAccess) {
    return (
      <div className="max-w-4xl mx-auto text-center">
          <div className="glass rounded-3xl p-12">
            <TrendUp size={64} className="mx-auto mb-6 text-[#6366F1]" />
            <h1 className="text-4xl font-bold text-white mb-4">
              Agent Analytics
            </h1>
            <p className="text-[#A0AEC0] text-lg mb-8">
              Gain insights into your AI workforce performance with detailed analytics
              and success metrics. This feature is available to Pro and Partner Pro members.
            </p>
            <button
              onClick={() => window.location.href = '/pricing'}
              className="relative overflow-hidden rounded-full group"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#6366F1] to-[#EC4899]"></div>
              <span className="relative z-10 flex items-center gap-2 px-8 py-4 text-white font-bold">
                Upgrade to Pro
              </span>
            </button>
          </div>
        </div>
    );
  }

  // Agent Detail View
  if (agentId && agentDetail) {
    const agent = agentDetail.agent;
    const byDay = agentDetail.by_day || [];
    const topThreads = agentDetail.top_threads || [];
    
    const totalRuns = byDay.reduce((sum, day) => sum + day.runs, 0);
    const totalSuccess = byDay.reduce((sum, day) => sum + day.success_runs, 0);
    const avgLatency = totalRuns > 0
      ? Math.round(byDay.reduce((sum, day) => sum + (day.avg_latency_ms * day.runs), 0) / totalRuns)
      : 0;
    const successRate = calculateSuccessRate(totalSuccess, totalRuns);

    return (
      <div className="max-w-7xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate('/partner-pro/ai-workforce/analytics')}
            className="flex items-center gap-2 text-[#6366F1] hover:text-[#4F46E5] mb-6"
          >
            <ArrowLeft size={20} />
            Back to Analytics
          </button>

            {/* Agent Header */}
            <div className="glass rounded-2xl p-6 mb-8">
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] flex items-center justify-center">
                    <Robot size={32} className="text-white" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-white mb-2">{agent.name}</h1>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        agent.status === 'active'
                          ? 'bg-[#14F195]/20 text-[#14F195]'
                          : 'bg-[#F59E0B]/20 text-[#F59E0B]'
                      }`}>
                        {agent.status}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setAgentToRun({ ...agent, id: agentId })}
                  data-testid="run-agent-analytics-btn"
                  className="px-6 py-3 rounded-full bg-[#6366F1] hover:bg-[#5558E6] text-white font-semibold transition-colors flex items-center gap-2"
                >
                  <Play size={16} weight="fill" />
                  Run Agent
                </button>
              </div>
            </div>

            {/* KPI Cards */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <AnalyticsKpiCard
                icon={Pulse}
                label="Total Runs"
                value={totalRuns.toLocaleString()}
                loading={loading}
              />
              <AnalyticsKpiCard
                icon={CheckCircle}
                label="Success Rate"
                value={successRate}
                unit="%"
                loading={loading}
              />
              <AnalyticsKpiCard
                icon={Clock}
                label="Avg Response"
                value={avgLatency}
                unit="ms"
                loading={loading}
              />
            </div>

            {/* Chart */}
            <div className="glass rounded-2xl p-6 mb-8">
              <h3 className="text-xl font-bold text-white mb-6">Performance Over Time</h3>
              {byDay.length > 0 ? (
                <RunsChart data={byDay} />
              ) : (
                <p className="text-[#A0AEC0] text-center py-8">No data for selected period</p>
              )}
            </div>

            {/* Top Threads */}
            <div className="glass rounded-2xl p-6">
              <h3 className="text-xl font-bold text-white mb-6">Top Conversation Threads</h3>
              {topThreads.length > 0 ? (
                <div className="space-y-3">
                  {topThreads.map((thread, idx) => (
                    <div
                      key={thread.thread_id}
                      className="flex items-center justify-between p-4 rounded-xl glass hover:bg-[rgba(255,255,255,0.08)] transition-all"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] flex items-center justify-center text-white font-bold text-sm">
                          {idx + 1}
                        </div>
                        <div className="flex-1">
                          <h4 className="text-white font-semibold">{thread.title}</h4>
                          <p className="text-[#A0AEC0] text-sm">
                            {thread.runs} runs · Last active {new Date(thread.last_run_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`/partner-pro/workspace/sessions?agentId=${agentId}&threadId=${thread.thread_id}`)}
                        className="px-4 py-2 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-[#6366F1] text-sm font-semibold flex items-center gap-2"
                      >
                        <ChatText size={16} />
                        Open
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#A0AEC0] text-center py-8">No threads yet</p>
              )}
            </div>

      {/* Run Agent Modal */}
      {agentToRun && (
        <RunAgentModal
          agent={agentToRun}
          isOpen={!!agentToRun}
          onClose={() => setAgentToRun(null)}
          onSuccess={(sessionId) => { console.log('Session completed:', sessionId); }}
        />
      )}
      </div>
    );
  }

  // Main Analytics Dashboard
  return (
    <div className="max-w-7xl mx-auto">
        {/* Header */}
        {true && (
          <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <TrendUp size={32} className="text-[#6366F1]" />
                <h1 className="text-4xl font-bold text-white">Agent Analytics</h1>
              </div>
              <p className="text-[#A0AEC0]">Understand how your AI workforce is performing</p>
            </div>

            {/* Date Range Picker */}
            <div className="flex gap-2">
              {['7', '30', '90'].map((days) => (
                <button
                  key={days}
                  onClick={() => setDateRange(days)}
                  className={`px-4 py-2 rounded-full font-semibold transition-all ${
                    dateRange === days
                      ? 'bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white'
                      : 'glass text-[#A0AEC0] hover:bg-[rgba(255,255,255,0.1)]'
                  }`}
                >
                  Last {days}d
                </button>
              ))}
            </div>
          </div>
        )}

        {error && (
            <div className="mb-6 p-4 rounded-xl bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.3)] flex items-center gap-3">
              <WarningCircle size={20} className="text-[#EF4444]" />
              <p className="text-[#EF4444] text-sm">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <CircleNotch size={48} className="text-[#6366F1] animate-spin" />
            </div>
          ) : (
            <>
              {/* KPI Cards */}
              {summary && (
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <AnalyticsKpiCard
                    icon={Pulse}
                    label="Total Runs"
                    value={(summary.totals?.runs ?? 0).toLocaleString()}
                    loading={loading}
                  />
                  <AnalyticsKpiCard
                    icon={CheckCircle}
                    label="Success Rate"
                    value={calculateSuccessRate(summary.totals?.success_runs ?? 0, summary.totals?.runs ?? 0)}
                    unit="%"
                    loading={loading}
                  />
                  <AnalyticsKpiCard
                    icon={Clock}
                    label="Avg Response Time"
                    value={Math.round(summary.totals?.avg_latency_ms ?? 0)}
                    unit="ms"
                    loading={loading}
                  />
                </div>
              )}

              {/* Chart */}
              {summary && summary.by_day && summary.by_day.length > 0 && (
                <div className="glass rounded-2xl p-6 mb-8">
                  <h3 className="text-xl font-bold text-white mb-6">Runs Over Time</h3>
                  <RunsChart data={summary.by_day} />
                </div>
              )}

              {/* Per-Agent Performance Table */}
              <div className="glass rounded-2xl p-6">
                <h3 className="text-xl font-bold text-white mb-6">Per-Agent Performance</h3>
                {agentsData.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[rgba(255,255,255,0.1)]">
                          <th className="text-left text-[#A0AEC0] font-semibold py-3 px-4">Agent</th>
                          <th className="text-left text-[#A0AEC0] font-semibold py-3 px-4">Status</th>
                          <th className="text-right text-[#A0AEC0] font-semibold py-3 px-4">Runs</th>
                          <th className="text-right text-[#A0AEC0] font-semibold py-3 px-4">Success Rate</th>
                          <th className="text-right text-[#A0AEC0] font-semibold py-3 px-4">Avg Response</th>
                          <th className="text-right text-[#A0AEC0] font-semibold py-3 px-4">Last Run</th>
                          <th className="text-right text-[#A0AEC0] font-semibold py-3 px-4">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {agentsData.map((agent) => (
                          <tr
                            key={agent.agent_id}
                            className="border-b border-[rgba(255,255,255,0.05)] hover:bg-[rgba(255,255,255,0.03)] transition-colors"
                          >
                            <td className="py-4 px-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] flex items-center justify-center">
                                  <Robot size={20} className="text-white" />
                                </div>
                                <span className="text-white font-semibold">{agent.name}</span>
                              </div>
                            </td>
                            <td className="py-4 px-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                agent.status === 'active'
                                  ? 'bg-[#14F195]/20 text-[#14F195]'
                                  : 'bg-[#F59E0B]/20 text-[#F59E0B]'
                              }`}>
                                {agent.status}
                              </span>
                            </td>
                            <td className="text-right text-white py-4 px-4">{(agent.total_runs ?? 0).toLocaleString()}</td>
                            <td className="text-right text-white py-4 px-4">
                              {calculateSuccessRate(agent.success_runs ?? 0, agent.total_runs ?? 0)}%
                            </td>
                            <td className="text-right text-white py-4 px-4">{Math.round(agent.avg_latency_ms ?? 0)}ms</td>
                            <td className="text-right text-[#A0AEC0] text-sm py-4 px-4">
                              {agent.last_run_at
                                ? new Date(agent.last_run_at).toLocaleDateString()
                                : 'Never'}
                            </td>
                            <td className="text-right py-4 px-4">
                              <button
                                onClick={() => {
                                  console.log('[AgentAnalyticsDashboard] View Details clicked for agent:', agent.agent_id);
                                  navigate(`/analytics/agents/${agent.agent_id}`);
                                }}
                                className="text-[#6366F1] hover:text-[#4F46E5] text-sm font-semibold"
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <TrendUp size={48} className="mx-auto mb-4 text-[#A0AEC0]" />
                    <h3 className="text-xl font-bold text-white mb-2">No Analytics Yet</h3>
                    <p className="text-[#A0AEC0] mb-6">Start using your agents to see performance data</p>
                    <div className="flex gap-3 justify-center">
                      <button
                        onClick={() => navigate('/partner-pro/ai-workforce/builder')}
                        className="px-6 py-3 rounded-full bg-[#6366F1] hover:bg-[#5558E6] text-white font-semibold transition-colors"
                      >
                        Create Agent
                      </button>
                      <button
                        onClick={() => navigate('/partner-pro/ai-workforce/agents')}
                        className="px-6 py-3 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white font-semibold"
                      >
                        View Agents
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
      </div>
  );
};

// Simple chart component using SVG
const RunsChart = ({ data }) => {
  if (!data || data.length === 0) return null;

  const maxRuns = Math.max(...data.map(d => d.runs), 1);
  const maxCost = Math.max(...data.map(d => d.cost_usd), 0.01);
  const width = 100; // percentage
  const height = 200; // pixels
  const padding = 20;

  const points = data.map((day, i) => {
    const x = (i / (data.length - 1)) * (width - 2 * padding) + padding;
    const y = height - (day.runs / maxRuns) * (height - 2 * padding) - padding;
    return `${x},${y}`;
  }).join(' ');

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height: '200px' }}>
        {/* Gradient definition - must come first */}
        <defs>
          <linearGradient id="runsChartGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#6366F1" />
            <stop offset="100%" stopColor="#EC4899" />
          </linearGradient>
        </defs>

        {/* Grid lines */}
        {[0, 25, 50, 75, 100].map(percent => {
          const y = height - (percent / 100) * (height - 2 * padding) - padding;
          return (
            <line
              key={percent}
              x1={padding}
              y1={y}
              x2={width - padding}
              y2={y}
              stroke="rgba(255,255,255,0.1)"
              strokeWidth="0.5"
            />
          );
        })}

        {/* Line */}
        <polyline
          points={points}
          fill="none"
          stroke="url(#runsChartGradient)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Points */}
        {data.map((day, i) => {
          const x = (i / (data.length - 1)) * (width - 2 * padding) + padding;
          const y = height - (day.runs / maxRuns) * (height - 2 * padding) - padding;
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r="1.5"
              fill="#6366F1"
            />
          );
        })}
      </svg>

      {/* X-axis labels */}
      <div className="flex justify-between mt-4 px-4">
        {data.filter((_, i) => i === 0 || i === Math.floor(data.length / 2) || i === data.length - 1).map((day, i) => (
          <span key={i} className="text-[#A0AEC0] text-xs">
            {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        ))}
      </div>
    </div>
  );
};

export default AgentAnalyticsDashboard;
